import { RECORD_ENTITY_LABELS, type NoteSource, type RecordEntity } from '@crm/contracts';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { NOTE_SOURCE_KIND } from './note-origin-filter.tsx';

const rowClass =
  'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-(--v2-dropdown-bg-hover)';

/** Kinds listed as a folding group in the tree; the others (the record, its company) are rows. */
const GROUPS: RecordEntity[] = ['contacts', 'deals', 'jobs', 'candidates'];

/** How a target reads as a row of the tree: "Project", "Company: Northwind Traders". */
export const noteTargetLabel = (src: NoteSource) =>
  src.entityType === 'projects' ? 'Project' : `${NOTE_SOURCE_KIND[src.entityType]}: ${src.label}`;

const same = (a: NoteSource, b: NoteSource | undefined) =>
  !!b && a.entityType === b.entityType && a.entityId === b.entityId;

/**
 * Where a note can go, as a tree: the project and its company as rows, then Contacts, Deals, Jobs
 * and Candidates folding their records (as the Stage filter's tree). Plain elements only, so it
 * also works inside the Meeting Companion's Picture-in-Picture window, where Radix cannot go.
 */
export function NoteTargetTree({
  sources,
  onPick,
  label,
  selected,
  query = '',
}: {
  sources: readonly NoteSource[];
  onPick: (src: NoteSource) => void;
  label: string;
  /** Marked in the tree; its group starts unfolded. */
  selected?: NoteSource | undefined;
  /** Lower-cased search: only matching records, their groups unfolded. Empty = everything. */
  query?: string;
}) {
  const [unfolded, setUnfolded] = useState<ReadonlySet<RecordEntity>>(
    () => new Set(selected && GROUPS.includes(selected.entityType) ? [selected.entityType] : []),
  );
  const hit = (text: string) => !query || text.toLowerCase().includes(query);
  const rows = sources.filter(
    (src) => !GROUPS.includes(src.entityType) && (hit(noteTargetLabel(src)) || hit(src.label)),
  );
  const groups = GROUPS.map((kind) => ({
    kind,
    items: sources.filter((src) => src.entityType === kind && hit(src.label)),
  })).filter((g) => g.items.length > 0);
  if (query && rows.length === 0 && groups.length === 0)
    return <p className="text-muted-foreground px-2 py-1.5 text-sm">No record matches.</p>;
  const fold = (kind: RecordEntity) =>
    setUnfolded((u) => {
      const next = new Set(u);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  const row = (src: NoteSource, text: string) => (
    <button
      type="button"
      className={`${rowClass} ${same(src, selected) ? 'font-medium' : ''}`}
      title={src.label}
      aria-current={same(src, selected) || undefined}
      onClick={() => onPick(src)}
    >
      <span className="min-w-0 truncate">{text}</span>
    </button>
  );

  return (
    <ul aria-label={label}>
      {rows.map((src) => (
        <li key={`${src.entityType}:${src.entityId}`}>{row(src, noteTargetLabel(src))}</li>
      ))}
      {groups.map(({ kind, items }) => {
        const open = !!query || unfolded.has(kind);
        return (
          <li key={kind}>
            <button
              type="button"
              className={`${rowClass} justify-between`}
              aria-expanded={open}
              onClick={() => fold(kind)}
            >
              <span>
                {RECORD_ENTITY_LABELS[kind]}{' '}
                <span className="text-muted-foreground text-xs">{items.length}</span>
              </span>
              {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
            </button>
            {open && (
              <ul aria-label={RECORD_ENTITY_LABELS[kind]} className="max-h-60 overflow-auto pl-5">
                {items.map((src) => (
                  <li key={src.entityId}>{row(src, src.label)}</li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}
